/**
 * Choosing a node's categories: the graph's categories as toggle buttons (on: the node is in
 * it), and a field to name a new one. Categories are only names, made by using them; there are
 * none at first.
 */
import { For, Show, useContext, type JSX } from "solid-js";
import { categoryVar, cleanCategories, sameCategory } from "./categories";
import { CategoryOrder } from "./ui";

/** The picker. `known` is the graph's categories; `value` the chosen ones, in order. */
export function CategoryPicker(props: {
  /** Prefix of the elements' ids (`<id>-new` is the field). */
  id: string;
  /** The chosen categories. */
  value: readonly string[];
  /** The categories offered (usually the graph's, in its order). */
  known: readonly string[];
  /** Called with the new list (cleaned: trimmed, each once). */
  onChange: (categories: string[]) => void;
}): JSX.Element {
  const order = useContext(CategoryOrder);
  /** Every category shown: the known ones, then chosen ones the graph does not use yet. */
  const shown = () => cleanCategories([...props.known, ...props.value]);
  const chosen = (name: string) => props.value.some((c) => sameCategory(c, name));
  /** Adds or removes one category. */
  const toggle = (name: string) => props.onChange(chosen(name) ? props.value.filter((c) => !sameCategory(c, name)) : cleanCategories([...props.value, name]));
  let input!: HTMLInputElement;
  /** Adds the typed category (an existing one is just chosen, in its usual spelling). */
  const add = () => {
    const typed = input.value.trim();
    if (!typed) return;
    const name = shown().find((c) => sameCategory(c, typed)) ?? typed;
    if (!chosen(name)) props.onChange(cleanCategories([...props.value, name]));
    input.value = "";
  };
  return (
    <div class="catpick" id={props.id}>
      <Show when={shown().length > 0}>
        <div class="seg">
          <For each={shown()}>
            {(name) => (
              <button type="button" class="seg-b" classList={{ on: chosen(name) }} aria-pressed={chosen(name)} data-category-pick={name} onClick={() => toggle(name)}>
                <i class="dot" style={{ background: `var(${categoryVar(order(), name)})` }} /> {name}
              </button>
            )}
          </For>
        </div>
      </Show>
      <div class="catnew">
        <input
          class="field"
          id={`${props.id}-new`}
          ref={input}
          maxlength="40"
          placeholder={shown().length ? "New category…" : "Name a category, for example Android"}
          aria-label="New category"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" class="btn" id={`${props.id}-add`} onClick={add}>
          Add
        </button>
      </div>
    </div>
  );
}
